import { MessageSquareQuote } from "lucide-react"

import { Badge } from "@/components/ui/badge"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"

function formatDate(value) {
  if (!value) return "—"
  return new Date(value).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  })
}

/**
 * Where a Member Story article came from, shown beside the editor the way a
 * Meeting's transcript is: the interview as the member actually answered it,
 * the draft versions and the changes they asked for, and any note that reached
 * the rewrite cap and came to the coach instead.
 *
 * Read-only. The coach edits the article; the interview is a record of what was
 * said and does not change.
 */
export function MemberStorySourcePanel({ source }) {
  if (!source) return null

  return (
    <Card>
      <CardHeader>
        <div className="flex flex-wrap items-center justify-between gap-2">
          <CardTitle className="flex items-center gap-2 text-sm">
            <MessageSquareQuote className="h-4 w-4 text-kf-red" aria-hidden="true" />
            Source: Member Story interview
          </CardTitle>
          <div className="flex items-center gap-2">
            {source.consentedAt && (
              <Badge
                variant="secondary"
                className="border-emerald-200 bg-emerald-100 text-emerald-800"
              >
                Consented {formatDate(source.consentedAt)}
              </Badge>
            )}
            <Badge variant="outline">{source.memberName}</Badge>
          </div>
        </div>
      </CardHeader>

      <CardContent className="space-y-6">
        {source.noteForCoach && (
          <div className="rounded-md border-l-2 border-kf-red bg-red-50/60 p-4">
            <p className="text-xs font-semibold uppercase tracking-wider text-kf-red">
              A note from {source.memberName}
            </p>
            <p className="mt-2 whitespace-pre-line text-sm text-zinc-700">
              {source.noteForCoach}
            </p>
            <p className="mt-2 text-xs text-zinc-500">
              They asked for more changes than the interview could make, so it
              came to you instead.
            </p>
          </div>
        )}

        {source.versions.length > 1 && (
          <div>
            <p className="text-xs font-semibold uppercase tracking-wider text-zinc-500">
              Draft history
            </p>
            <ol className="mt-3 space-y-2">
              {source.versions.map((version) => (
                <li
                  key={version.version}
                  className="flex flex-wrap items-baseline gap-x-2 gap-y-1 text-sm"
                >
                  <span className="font-mono text-xs text-zinc-500">
                    v{version.version}
                  </span>
                  <span className="text-zinc-700">{version.title}</span>
                  {version.changeNote && (
                    <span className="text-xs text-zinc-500">
                      — asked for: “{version.changeNote}”
                    </span>
                  )}
                </li>
              ))}
            </ol>
          </div>
        )}

        <div>
          <p className="text-xs font-semibold uppercase tracking-wider text-zinc-500">
            The interview
          </p>
          <dl className="mt-3 max-h-96 space-y-4 overflow-y-auto pr-2">
            {source.transcript.map((entry, index) => (
              <div key={`${entry.position}-${index}`}>
                <dt
                  className={
                    entry.isFollowup
                      ? "text-sm italic text-zinc-500"
                      : "text-sm font-medium text-zinc-900"
                  }
                >
                  {entry.questionText}
                </dt>
                <dd className="mt-1 whitespace-pre-line text-sm leading-relaxed text-zinc-600">
                  {entry.answerText}
                </dd>
              </div>
            ))}
          </dl>
        </div>
      </CardContent>
    </Card>
  )
}
